import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * RAID log — APM-701, spec mục 12.
 *
 * Bốn loại (Risk / Action / Issue / Decision) nằm **một bảng**, vì đó là cách
 * người dùng nhìn chúng: một danh sách lọc theo loại, và một rủi ro xảy ra thật
 * thì escalate thành issue chỉ bằng một thao tác. Tách bốn bảng thì thao tác đó
 * thành "chép tay sang chỗ khác", và không còn đường nào lần lại từ issue về
 * rủi ro đã cảnh báo trước.
 *
 * Đổi lại: mỗi loại chỉ dùng một phần các cột. Cột nào thuộc loại nào là
 * `RAID_FIELDS` dưới đây — server chuẩn hoá theo đúng bảng này lúc ghi, nên
 * không có chuyện một ISSUE còn giữ `probability` sau khi escalate từ RISK.
 */
/**
 * ⚠️ Có **hai** cách khai triển chữ RAID, và cả hai đều chuẩn.
 *
 * - Risks · **Actions** · Issues · **Decisions** — lối quản trị dự án chạy
 *   thường ngày, và là lối app này dùng.
 * - Risks · **Assumptions** · Issues · **Dependencies** — lối của các mẫu RAID
 *   log cổ điển, thiên về ghi chép để soát lại.
 *
 * `ASSUMPTION` thêm ở APM-RAID-f vì nó thật sự thiếu: một giả định chưa được
 * kiểm chứng là nguồn rủi ro phổ biến nhất, và không có chỗ nào để ghi nó.
 *
 * `DEPENDENCY` **cố ý không thêm**: phụ thuộc đã là công dân hạng nhất ở bảng
 * `task_dependency` (có loại quan hệ, có độ trễ, nuôi đường găng). Thêm một
 * loại RAID cùng tên là hai chỗ ghi cùng một khái niệm, và chúng sẽ lệch nhau
 * ngay lần đầu ai đó chỉ sửa một bên. Cột "việc liên quan" (APM-RAID-e) đã trỏ
 * thẳng sang phụ thuộc thật.
 */
export const RaidType = z.enum(['RISK', 'ASSUMPTION', 'ACTION', 'ISSUE', 'DECISION']);
export const RaidStatus = z.enum([
    'open',
    'in_progress',
    /** RISK: đã có biện pháp giảm thiểu và biện pháp đó đang có tác dụng. */
    'mitigated',
    /** ISSUE: đã xử lý xong. */
    'resolved',
    /** RISK: đã xảy ra thật ⇒ có một ISSUE trỏ về nó. Chỉ `escalate` đặt được. */
    'escalated',
    /** DECISION: đã ghi nhận. Quyết định không có vòng đời nào khác. */
    'recorded',
    'closed',
]);
/**
 * Trạng thái mà **người dùng** đặt được cho từng loại.
 *
 * `escalated` cố ý không có mặt: nó là kết quả của thao tác escalate, và cho
 * đặt tay thì sẽ có RISK mang trạng thái `escalated` mà không issue nào trỏ về.
 */
export const RAID_STATUSES = {
    RISK: ['open', 'mitigated', 'closed'],
    /*
      Giả định có ba chặng, không phải hai: `open` là *chưa kiểm chứng*,
      `mitigated` mượn nghĩa **đã xác nhận đúng**, `closed` là hết hiệu lực.
  
      Không thêm trạng thái riêng cho "hoá ra sai": lúc ấy nó **là** một vấn đề,
      và đường đúng là escalate sang ISSUE — cùng đường mà RISK đã đi.
    */
    ASSUMPTION: ['open', 'mitigated', 'closed'],
    ACTION: ['open', 'in_progress', 'closed'],
    ISSUE: ['open', 'in_progress', 'resolved', 'closed'],
    DECISION: ['recorded'],
};
export const RAID_INITIAL_STATUS = {
    RISK: 'open',
    ASSUMPTION: 'open',
    ACTION: 'open',
    ISSUE: 'open',
    DECISION: 'recorded',
};
/** Cột nào thuộc loại nào. Server xoá sạch những cột ngoài danh sách của loại. */
export const RAID_FIELDS = {
    RISK: ['probability', 'impact', 'ownerId', 'dueDate', 'mitigation'],
    /*
      Giả định **không** dùng ma trận xác suất: nó không phải chuyện "có thể xảy
      ra hay không" mà là "đang tin điều này là đúng". Cái đáng đo là **ảnh hưởng
      nếu nó sai**, nên chỉ `impact`.
  
      `dueDate` ở đây là **mốc soát lại** — một giả định không được soát lại thì
      chỉ là một rủi ro chưa ai gọi tên. `mitigation` là "làm gì nếu nó sai".
    */
    ASSUMPTION: ['impact', 'ownerId', 'dueDate', 'mitigation'],
    ACTION: ['ownerId', 'dueDate'],
    ISSUE: ['impact', 'ownerId', 'dueDate', 'mitigation'],
    DECISION: ['ownerId', 'decidedAt', 'rationale', 'affectedScope'],
};
export function raidHasField(type, field) {
    return RAID_FIELDS[type].includes(field);
}
// ── Ma trận xác suất × ảnh hưởng ────────────────────────────────────────────
/** Thang 1–5 cho cả xác suất và ảnh hưởng ⇒ score 1–25. */
export const RaidScale = z.number().int().min(1).max(5);
/**
 * Ngưỡng "rủi ro cao": vượt qua nó thì PM nhận cảnh báo (spec mục 12).
 *
 * Chỉ cảnh báo lúc **vượt ngưỡng**, không phải mỗi lần lưu: sửa mô tả một rủi
 * ro đã đỏ mà lại bắn mail thì người ta tắt thông báo, và cảnh báo tiếp theo —
 * cái thật sự quan trọng — không ai đọc.
 */
export const RAID_HIGH_RISK_SCORE = 15;
export function riskScore(probability, impact) {
    if (probability === null || impact === null)
        return null;
    return probability * impact;
}
/**
 * Score → màu RAG. Ngưỡng nằm ở đây, dùng chung FE↔BE: hai bảng ngưỡng ở hai
 * bên sẽ lệch nhau, và lúc đó bảng RAID tô vàng còn email báo đỏ.
 */
export function riskRag(score) {
    if (score === null)
        return 'unknown';
    if (score >= RAID_HIGH_RISK_SCORE)
        return 'red';
    if (score >= 8)
        return 'amber';
    return 'green';
}
// ── Input ───────────────────────────────────────────────────────────────────
const RaidBody = {
    title: z.string().trim().min(1).max(300),
    description: z.string().max(5_000).nullable().optional(),
    ownerId: z.uuid().nullable().optional(),
    probability: RaidScale.nullable().optional(),
    /** RISK: mức ảnh hưởng. ISSUE: mức nghiêm trọng — cùng thang, cùng cột. */
    impact: RaidScale.nullable().optional(),
    /** ACTION: deadline. RISK: mốc xem lại. */
    dueDate: CalendarDate.nullable().optional(),
    /** DECISION: ngày ra quyết định. Lùi được về quá khứ — họp tuần trước mới ghi. */
    decidedAt: CalendarDate.nullable().optional(),
    /** RISK: kế hoạch giảm thiểu. ISSUE: hướng xử lý. */
    mitigation: z.string().max(5_000).nullable().optional(),
    /** DECISION: lý do / căn cứ. */
    rationale: z.string().max(5_000).nullable().optional(),
    /** DECISION: phạm vi ảnh hưởng. */
    affectedScope: z.string().max(500).nullable().optional(),
};
/**
 * Bắt buộc theo loại — spec mục 12 liệt kê thẳng những gì mỗi loại "cần".
 *
 * Ép ngay ở contract chứ không nhắc nhở trong UI: một RISK không có xác suất và
 * ảnh hưởng thì không có score, và không có score thì nó tụt xuống đáy mọi
 * danh sách xếp theo mức độ — tức là đúng thứ cần nhìn lại thành thứ không ai
 * nhìn thấy.
 */
export const CreateRaidInput = z
    .object({ type: RaidType, ...RaidBody })
    .superRefine((value, ctx) => {
    const require = (field, message) => {
        if (value[field] === undefined || value[field] === null) {
            ctx.addIssue({ code: 'custom', path: [field], message });
        }
    };
    if (value.type === 'RISK') {
        require('probability', 'Rủi ro phải có xác suất xảy ra');
        require('impact', 'Rủi ro phải có mức ảnh hưởng');
    }
    if (value.type === 'ISSUE')
        require('impact', 'Vấn đề phải có mức nghiêm trọng');
    if (value.type === 'ACTION') {
        require('ownerId', 'Hành động phải có người phụ trách');
        require('dueDate', 'Hành động phải có hạn hoàn thành');
    }
    if (value.type === 'DECISION') {
        require('ownerId', 'Quyết định phải ghi ai đã quyết');
        require('decidedAt', 'Quyết định phải ghi ngày quyết');
    }
});
/**
 * Không có `type`: đổi loại của một hạng mục đã ghi là xoá dấu vết của nó —
 * số hiệu `R-3` đã nằm trong biên bản họp. Đường duy nhất từ RISK sang ISSUE là
 * `escalate`, và nó **tạo hạng mục mới** trỏ về rủi ro gốc.
 */
export const UpdateRaidInput = z.object(RaidBody).partial();
export const ChangeRaidStatusInput = z.object({
    status: RaidStatus,
    /** Bắt buộc khi đóng: "vì sao đóng" là thứ người mới vào dự án đi tìm. */
    note: z.string().max(2_000).optional(),
});
export const EscalateRiskInput = z.object({
    /** Mức nghiêm trọng của issue. Bỏ trống ⇒ lấy `impact` của rủi ro gốc. */
    severity: RaidScale.optional(),
});
// ── View ────────────────────────────────────────────────────────────────────
/**
 * Quan hệ giữa một hạng mục RAID và một công việc — APM-RAID-e.
 *
 * ## Vì sao ba loại chứ không phải một
 *
 * "Liên quan" chung chung thì mỗi người hiểu một kiểu, và một danh sách toàn
 * chữ "liên quan" không lọc ra được gì. Ba loại này tách đúng ba câu hỏi khác
 * nhau mà PM thật sự hỏi:
 *
 * - `affects` — *có thể* làm việc này trượt. Không chặn; việc vẫn chạy được.
 * - `blocks` — đang **đứng chắn**. Chỉ ISSUE mới có nghĩa ở đây, và nó là thứ
 *   phải xử lý hôm nay chứ không phải theo dõi.
 * - `mitigates` — việc này **là cách xử lý** hạng mục. Đây là quan hệ nói lên
 *   rủi ro đang thực sự được làm gì đó, hay chỉ đang được ghi vào sổ.
 */
export const RaidLinkRelation = z.enum(['affects', 'blocks', 'mitigates']);
export const LinkRaidTaskInput = z.object({
    /**
     * `null` = **toàn dự án** — APM-ORG-h5.
     *
     * Phần lớn rủi ro không chạm vào một việc nào cụ thể ("nhà cung cấp chậm
     * giao hạ tầng"), và ép người dùng chọn một việc đại diện thì con số "việc
     * này đang gánh mấy rủi ro" sai ngay từ dòng đầu.
     */
    taskId: z.uuid().nullable(),
    relation: RaidLinkRelation,
});
/** Một liên kết, nhìn từ **phía hạng mục RAID**. */
export const RaidTaskLinkView = z.object({
    id: z.uuid(),
    relation: RaidLinkRelation,
    /** Cả bốn trường dưới cùng `null` khi cạnh này trỏ vào **toàn dự án**. */
    taskId: z.uuid().nullable(),
    taskKey: z.string().nullable(),
    taskTitle: z.string().nullable(),
    /** Trạng thái việc — để thấy ngay rủi ro đang treo trên một việc đã xong hay chưa. */
    taskStatusCategory: z.string().nullable(),
});
/** Một liên kết, nhìn từ **phía công việc** — popup chi tiết task đọc cái này. */
export const TaskRaidLinkView = z.object({
    id: z.uuid(),
    relation: RaidLinkRelation,
    raidItemId: z.uuid(),
    raidCode: z.string(),
    raidType: RaidType,
    raidTitle: z.string(),
    raidStatus: RaidStatus,
    /** `probability × impact` với RISK. `null` với loại không dùng ma trận. */
    raidScore: z.number().int().nullable(),
});
export const RaidItemView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    /** Mã hiển thị: `R-3`, `A-4`, `I-5`, `D-6`. Một dải số cho cả bốn loại. */
    code: z.string(),
    number: z.number().int().positive(),
    type: RaidType,
    title: z.string(),
    description: z.string().nullable(),
    status: RaidStatus,
    ownerId: z.uuid().nullable(),
    ownerName: z.string().nullable(),
    probability: z.number().int().nullable(),
    impact: z.number().int().nullable(),
    /** `probability × impact`, server tính. Null khi loại không dùng ma trận. */
    score: z.number().int().nullable(),
    dueDate: CalendarDate.nullable(),
    decidedAt: CalendarDate.nullable(),
    mitigation: z.string().nullable(),
    rationale: z.string().nullable(),
    affectedScope: z.string().nullable(),
    /** ISSUE sinh ra từ escalate: rủi ro gốc. */
    sourceRiskId: z.uuid().nullable(),
    sourceRiskCode: z.string().nullable(),
    /** RISK đã escalate: issue mà nó biến thành. */
    escalatedIssueId: z.uuid().nullable(),
    escalatedIssueCode: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    closedAt: z.iso.datetime().nullable(),
    /**
     * Việc mà hạng mục này chạm vào — APM-RAID-e.
     *
     * Trả kèm ngay trong sổ chứ không để client hỏi thêm một vòng cho mỗi hàng:
     * một sổ RAID ba mươi hàng sẽ thành ba mươi request, và cột "việc liên quan"
     * là cột người ta quét mắt xuống chứ không phải cột bấm vào từng ô.
     */
    links: z.array(RaidTaskLinkView),
});
export const RaidLogView = z.object({
    projectId: z.uuid(),
    items: z.array(RaidItemView),
    /** Số hạng mục đang mở theo loại — chip đếm trên đầu bảng. */
    openByType: z.record(RaidType, z.number().int().nonnegative()),
    /** Rủi ro đang mở có score ≥ ngưỡng cao. Dashboard `APM-801` đọc lại con số này. */
    highRiskCount: z.number().int().nonnegative(),
});
export const EscalateRiskResult = z.object({
    risk: RaidItemView,
    issue: RaidItemView,
});
//# sourceMappingURL=raid.js.map